import brainAssistantsAPI from '../../../api/pipelines/brain/assistants';
import brainDocumentsAPI from '../../../api/pipelines/brain/documents';

const state = {
  assistants: [],
  loading: false,
  error: null,
  // documents keyed by assistantId
  documents: {},
  documentsLoading: {},
  documentsError: {},
  // tools keyed by assistantId: { catalog: [], enabled_tools: [] }
  tools: {},
};

const getters = {
  allAssistants: s => s.assistants,
  isLoading: s => s.loading,
  error: s => s.error,
  documentsByAssistant: s => assistantId => s.documents[assistantId] || [],
  isDocumentsLoading: s => assistantId => !!s.documentsLoading[assistantId],
  documentsError: s => assistantId => s.documentsError[assistantId] || null,
  toolsForAssistant: s => assistantId => s.tools[assistantId] || { catalog: [], enabled_tools: [] },
};

const mutations = {
  SET_ASSISTANTS(state, assistants) {
    state.assistants = assistants;
  },
  ADD_ASSISTANT(state, assistant) {
    state.assistants.push(assistant);
  },
  UPDATE_ASSISTANT(state, updatedAssistant) {
    const index = state.assistants.findIndex(a => a.id === updatedAssistant.id);
    if (index !== -1) {
      state.assistants.splice(index, 1, updatedAssistant);
    }
  },
  REMOVE_ASSISTANT(state, id) {
    state.assistants = state.assistants.filter(a => a.id !== id);
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_DOCUMENTS(state, { assistantId, documents }) {
    state.documents = { ...state.documents, [assistantId]: documents };
  },
  UPDATE_DOCUMENT(state, { assistantId, document }) {
    const list = state.documents[assistantId] || [];
    const idx = list.findIndex(d => d.path === document.path);
    if (idx !== -1) {
      const updated = [...list];
      updated.splice(idx, 1, document);
      state.documents = { ...state.documents, [assistantId]: updated };
    } else {
      state.documents = { ...state.documents, [assistantId]: [...list, document] };
    }
  },
  SET_DOCUMENTS_LOADING(state, { assistantId, loading }) {
    state.documentsLoading = { ...state.documentsLoading, [assistantId]: loading };
  },
  SET_DOCUMENTS_ERROR(state, { assistantId, error }) {
    state.documentsError = { ...state.documentsError, [assistantId]: error };
  },
  SET_TOOLS(state, { assistantId, catalog, enabled_tools }) {
    state.tools = { ...state.tools, [assistantId]: { catalog, enabled_tools } };
  },
  SET_TOOLS_ENABLED(state, { assistantId, enabled_tools }) {
    const existing = state.tools[assistantId] || { catalog: [] };
    state.tools = { ...state.tools, [assistantId]: { ...existing, enabled_tools } };
  },
};

const actions = {
  async fetchAssistants({ commit, rootGetters }) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const { data } = await brainAssistantsAPI.list(accountId);
      commit('SET_ASSISTANTS', Array.isArray(data) ? data : data.data || []);
    } catch (e) {
      commit('SET_ERROR', e.message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createAssistant({ commit, rootGetters }, assistantData) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    const { data } = await brainAssistantsAPI.create(accountId, assistantData);
    commit('ADD_ASSISTANT', data);
    return data;
  },

  async updateAssistant({ commit, rootGetters }, { id, ...assistantData }) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    const { data } = await brainAssistantsAPI.update(accountId, id, assistantData);
    commit('UPDATE_ASSISTANT', data);
    return data;
  },

  async deleteAssistant({ commit, rootGetters }, id) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    await brainAssistantsAPI.delete(accountId, id);
    commit('REMOVE_ASSISTANT', id);
  },

  async syncAgentsFromOpenclaw({ commit, rootGetters }) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    const { data } = await brainAssistantsAPI.syncAgents(accountId);
    // Refresh assistants list after sync
    const listRes = await brainAssistantsAPI.list(accountId);
    commit('SET_ASSISTANTS', Array.isArray(listRes.data) ? listRes.data : listRes.data.data || []);
    return data;
  },

  // Alias canônico esperado pelo sidebar/UI
  async syncAgents(ctx) {
    return ctx.dispatch('syncAgentsFromOpenclaw');
  },

  // ── Documents ──────────────────────────────────────────────────────────────

  async fetchDocuments({ commit, rootGetters }, assistantId) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    commit('SET_DOCUMENTS_LOADING', { assistantId, loading: true });
    commit('SET_DOCUMENTS_ERROR', { assistantId, error: null });
    try {
      const { data } = await brainDocumentsAPI.list(accountId, assistantId);
      commit('SET_DOCUMENTS', {
        assistantId,
        documents: Array.isArray(data) ? data : data.data || [],
      });
    } catch (e) {
      commit('SET_DOCUMENTS_ERROR', { assistantId, error: e.message });
    } finally {
      commit('SET_DOCUMENTS_LOADING', { assistantId, loading: false });
    }
  },

  async autosaveDocument({ commit, rootGetters }, { assistantId, path, content }) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    const { data } = await brainDocumentsAPI.autosave(accountId, assistantId, path, content);
    commit('UPDATE_DOCUMENT', { assistantId, document: data });
    return data;
  },

  async pushDocument({ commit, rootGetters }, { assistantId, path, message, baseSha }) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    const { data } = await brainDocumentsAPI.push(accountId, assistantId, path, {
      message,
      baseSha,
    });
    // After push, document draft is cleared; refresh the document
    const docRes = await brainDocumentsAPI.get(accountId, assistantId, path);
    commit('UPDATE_DOCUMENT', { assistantId, document: docRes.data });
    return data;
  },

  async discardDocument({ commit, rootGetters }, { assistantId, path }) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    const { data } = await brainDocumentsAPI.discard(accountId, assistantId, path);
    commit('UPDATE_DOCUMENT', { assistantId, document: data });
    return data;
  },

  // ── Tools ──────────────────────────────────────────────────────────────────

  async fetchTools({ commit, rootGetters }, assistantId) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    const { data } = await brainAssistantsAPI.getTools(accountId, assistantId);
    commit('SET_TOOLS', {
      assistantId,
      catalog: data.catalog || [],
      enabled_tools: data.enabled_tools || [],
    });
    return data;
  },

  async updateTools({ commit, rootGetters }, { assistantId, enabledTools }) {
    const accountId = rootGetters['auth/getCurrentAccount']?.id;
    if (!accountId) return;
    await brainAssistantsAPI.updateTools(accountId, assistantId, enabledTools);
    commit('SET_TOOLS_ENABLED', { assistantId, enabled_tools: enabledTools });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
